import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { type IntlService, t } from 'ember-intl';
import PowerSelect from 'ember-power-select/components/power-select';

import Button from 'umg-ember-backend/components/ui/button';
import type {
  Locale,
  default as LocaleService,
} from 'umg-ember-backend/services/locale';
import type MsalService from 'umg-ember-backend/services/msal';
import type SessionService from 'umg-ember-backend/services/session';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import styles from './styles.module.css';

const bem = getBem(styles);

interface SignInSignature {
  Element: HTMLDivElement;
}

interface AuthenticationError {
  payload?: {
    errors?: {
      message?: string;
    };
  };
}

export default class SignInComponentComponent extends Component<SignInSignature> {
  // Services
  @service declare intl: IntlService;
  @service declare session: SessionService;
  @service declare locale: LocaleService;
  @service declare msal: MsalService;

  // Credential authentication
  @tracked username: string | undefined;
  @tracked password: string | undefined;
  @tracked errorMessage: string | undefined;

  updatePassword = (event: Event) => {
    this.password = (event.target as HTMLInputElement).value;
  };

  updateUsername = (event: Event) => {
    this.username = (event.target as HTMLInputElement).value;
  };

  signIn = async (event: SubmitEvent) => {
    event.preventDefault();

    const authenticator = 'authenticator:umg';
    const credentials = {
      username: this.username,
      password: this.password,
    };

    try {
      await this.session.authenticate(authenticator, credentials);
    } catch (error) {
      const authenticationError = error as AuthenticationError;

      if (
        authenticationError.payload?.errors?.message ===
        'User has been deactivated.'
      ) {
        this.errorMessage = this.intl.t('sign_in.errors.deactivated');
      } else {
        this.errorMessage = this.intl.t('sign_in.errors.default');
      }
    }
  };

  // Microsoft authentication
  signInWithMicrsoft = () => {
    this.msal.loginRedirect();
  };

  // Locale selection
  setLocale = (locale: Locale) => {
    this.locale.setup(locale.code);
  };

  // Template
  <template>
    <div class={{(bem)}} ...attributes>
      <div class={{bem 'wrapper'}}>
        <div class={{bem 'form'}}>
          <img
            class={{bem 'logo'}}
            src='/assets/images/logo_univseral_music_inverted.svg'
            alt={{t 'sign_in.logo_alt'}}
          />
          <h1 class={{bem 'title'}}>
            {{t 'sign_in.title'}}
          </h1>
          <p class={{bem 'message'}}>
            {{t 'sign_in.message'}}
          </p>

          <Button
            @label={{t 'sign_in.with_microsoft'}}
            @style='outline'
            @displayBlock={{true}}
            @type='button'
            {{on 'click' this.signInWithMicrsoft}}
          />

          <div class={{bem 'or'}}>
            {{t 'sign_in.or'}}
          </div>
          <form {{on 'submit' this.signIn}}>
            {{#if this.errorMessage}}
              <div class={{bem 'error'}}>
                {{this.errorMessage}}
              </div>
            {{/if}}

            <div class={{bem 'field'}}>
              <label for='username' class={{bem 'field-label'}}>
                {{t 'sign_in.fields.name'}}
              </label>
              <input
                type='text'
                value={{this.username}}
                class={{bem 'field-input'}}
                id='username'
                placeholder={{t 'sign_in.placeholders.name'}}
                {{on 'input' this.updateUsername}}
              />
            </div>
            <div class={{bem 'field'}}>
              <label for='password' class={{bem 'field-label'}}>
                {{t 'sign_in.fields.password'}}
              </label>
              <input
                type='password'
                value={{this.password}}
                class={{bem 'field-input'}}
                id='password'
                placeholder={{t 'sign_in.placeholders.password'}}
                {{on 'input' this.updatePassword}}
              />
            </div>

            <Button
              @displayBlock={{true}}
              @label={{t 'sign_in.submit'}}
              @style='outline'
              @type='submit'
            />
          </form>
        </div>
        <PowerSelect
          @options={{this.locale.availableLocales}}
          @selected={{this.locale.currentLocale}}
          @searchEnabled={{false}}
          @onChange={{this.setLocale}}
          @triggerClass={{bem 'locale-trigger'}}
          @dropdownClass={{bem 'locale-dropdown'}}
          as |locale|
        >
          {{locale.name}}
        </PowerSelect>
      </div>
    </div>
  </template>
}
